import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { DATE_PIPE_DEFAULT_TIMEZONE } from '@angular/common';

import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';

import { TicketComponent } from './page/ticket/ticket.component';
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { CustomTranslateLoader } from './services/custom-translate-loader.service';
import { InquiryComponent } from './page/inquiry/inquiry.component';
import en from '@angular/common/locales/en';
import { FormsModule } from '@angular/forms';
import { NgZorroAntdModule } from './modules/app-common/ng-zorro-antd.module';
import { AppCommonModule } from './modules/app-common/app-common.module';
import { registerLocaleData } from '@angular/common';
import zh from '@angular/common/locales/zh';
import { UserInfoComponent } from './components/user-info/user-info.component';
import { LangComponent } from './components/lang/lang.component';
import { MobileTicketComponent } from './mobile/mobile-ticket/mobile-ticket.component';
import { MobileInquiryComponent } from './mobile/mobile-inquiry/mobile-inquiry.component';
import { environment } from 'src/environments/environment';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import {
  MsalModule,
  MsalRedirectComponent,
  MsalInterceptor,
  MsalService,
  MsalGuard,
  MsalBroadcastService,
} from '@azure/msal-angular'; // Import MsalInterceptor
import { InteractionType,LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { MobileLogComponent } from './mobile/mobile-log/mobile-log.component';
import { MobileFilterComponent } from './mobile/mobile-filter/mobile-filter.component';
import { DeviceComponent } from './components/device/device.component';

// External integration components removed
import { TestService } from './services/test.service';
import { VdiService } from './services/vdi.service';
import { LoggingInterceptor } from './interceptors/logging.interceptor';

// Auth Testing Components and Services
import { AuthTestComponent } from './components/auth-test/auth-test.component';
import { AuthTestingService } from './services/auth-testing.service';
// Conditional MSAL Interceptor
import { ConditionalMsalInterceptor } from './interceptors/conditional-msal.interceptor';

// VM Status Display Component
import { VmStatusDisplayComponent } from './components/vm-status/vm-status-display.component';

// VDI Components
import { VdiApprovalStatusComponent } from './components/vdi-approval-status/vdi-approval-status.component';
import { RdpClientModule } from './components/rdp-client/rdp-client.module';

// Define API scope constants for different access levels
const API_USER_ACCESS_SCOPE = environment.azureAuthenticationConfig.scopes.userAccess;
const API_HELPDESK_SCOPE = environment.azureAuthenticationConfig.scopes.helpdesk;
const API_ALL_SCOPES = environment.azureAuthenticationConfig.scopes.all;

const isIE =
  window.navigator.userAgent.indexOf('MSIE ') > -1 ||
  window.navigator.userAgent.indexOf('Trident/') > -1;
registerLocaleData(zh);
import { NZ_I18N, en_US } from 'ng-zorro-antd/i18n';
import { CommoncomponentsModule } from './components/commoncomponents.module';

import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzTypographyModule } from 'ng-zorro-antd/typography';

function i18nHttpLoaderFactor(httpClient: HttpClient) {
  // Use custom loader to handle wrapped response format
  return new CustomTranslateLoader(httpClient);
}
@NgModule({
  declarations: [
    AppComponent,
    TicketComponent,
    InquiryComponent,
    UserInfoComponent,
    LangComponent,
    MobileTicketComponent,
    MobileInquiryComponent,
    MobileLogComponent,
    DeviceComponent,
    MobileFilterComponent,

    // Auth Testing Components
    AuthTestComponent,

    // VM Status Display Component
    VmStatusDisplayComponent,

    // VDI Components
    VdiApprovalStatusComponent,
  ],
  imports: [
    CommoncomponentsModule,
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    HttpClientModule,
    PdfViewerModule,
    RdpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: i18nHttpLoaderFactor,
        deps: [HttpClient]
      }
    }),
    FormsModule,
    NgZorroAntdModule,
    NzButtonModule,
    NzTypographyModule,
    NzModalModule,
    AppCommonModule,
    MsalModule.forRoot(
      new PublicClientApplication({
        auth: {
          clientId: environment.azureAuthenticationConfig.clientId,
          authority: environment.azureAuthenticationConfig.authority,
          redirectUri: environment.azureAuthenticationConfig.redirectUri,
          navigateToLoginRequestUrl: false,
        },
        cache: {
          cacheLocation: 'localStorage',
          storeAuthStateInCookie: isIE,
        },
        system: {
          allowNativeBroker: false,
          loggerOptions: {
            loggerCallback: (logLevel, message) => {
              console.log('MSAL Log:', message);
            },
            piiLoggingEnabled: false,
            logLevel: LogLevel.Verbose,
          },
          // Increase iframe timeout to prevent monitor_window_timeout errors
          iframeHashTimeout: 15000, // 15 seconds instead of default 6 seconds
        },
      }),
      {
        interactionType: InteractionType.Redirect, // MSAL Guard Configuration
        authRequest: {
          scopes: API_ALL_SCOPES,
        },
        loginFailedRoute: '/' // Redirect to home page on login failure
      },
      {
        interactionType: InteractionType.Redirect, // MSAL Interceptor Configuration
        protectedResourceMap: new Map([
          // Only protect endpoints that actually require authentication
          // Remove broad wildcard patterns that cause premature authentication attempts

          // Graph API endpoints
          ['https://graph.microsoft.com/v1.0/me', ['user.read']],

          // Only specific protected endpoints - avoid wildcards that cause loops
          [`${environment.apiEndpoint}api/Ticket/GetTicketPage`, [API_HELPDESK_SCOPE]],
          [`${environment.apiEndpoint}api/Ticket/GetTicketByID`, [API_HELPDESK_SCOPE]],
          [`${environment.apiEndpoint}api/Ticket/CreateTicket`, [API_HELPDESK_SCOPE]],
          [`${environment.apiEndpoint}api/Ticket/UpdateTicket`, [API_HELPDESK_SCOPE]],
          [`${environment.apiEndpoint}api/Ticket/InsertOrUpdateTicket`, [API_HELPDESK_SCOPE]],
          [`${environment.apiEndpoint}api/Ticket/AcceptTicket`, [API_HELPDESK_SCOPE]],
          [`${environment.apiEndpoint}api/Ticket/ReassignTicket`, [API_HELPDESK_SCOPE]],

          // Authentication endpoints that require authentication
          [`${environment.apiEndpoint}api/Auth/GetAccessRight`, [API_USER_ACCESS_SCOPE]],
          [`${environment.apiEndpoint}api/AADUser/GetAllUser`, [API_USER_ACCESS_SCOPE]],
          [`${environment.apiEndpoint}api/AADUser/GetUserPhoto`, [API_USER_ACCESS_SCOPE]],
          [`${environment.apiEndpoint}api/AADUser/GetAccessRight`, [API_USER_ACCESS_SCOPE]],

          // UserTeam endpoints that require authentication
          [`${environment.apiEndpoint}api/UserTeam/GetSupportTeam/`, [API_USER_ACCESS_SCOPE]],
          [`${environment.apiEndpoint}api/UserTeam/GetSupportTeamByUser`, [API_USER_ACCESS_SCOPE]],

          // Configuration endpoints that need authentication
          [`${environment.apiEndpoint}api/CategoryTeamMapConfig/InsertCategoryTeamMapByTeam`, [API_HELPDESK_SCOPE]],
          [`${environment.apiEndpoint}api/CategoryTeamMapConfig/InsertCategoryTeamMapByCategory`, [API_HELPDESK_SCOPE]],

          // Resolution Type endpoint that requires authentication
          [`${environment.apiEndpoint}api/ResolutionType/GetResolutionType/`, [API_USER_ACCESS_SCOPE]],

          // TicketStatus endpoint that requires authentication
          [`${environment.apiEndpoint}api/TicketStatus/GetTicketStatus/`, [API_USER_ACCESS_SCOPE]],

          // Ticket endpoints that require authentication
          [`${environment.apiEndpoint}api/Ticket/`, [API_USER_ACCESS_SCOPE]],
          [`${environment.apiEndpoint}api/Attachment/Attachments/`, [API_USER_ACCESS_SCOPE]],
          [`${environment.apiEndpoint}api/TicketRemark/GetTicketRemarkPage/`, [API_USER_ACCESS_SCOPE]],

          // AVD Provisioning endpoints that require authentication
          [`${environment.apiEndpoint}api/provisioning/`, [API_USER_ACCESS_SCOPE]],

          // VDI Approval endpoints that require authentication
          [`${environment.apiEndpoint}api/vdi-approval/`, [API_USER_ACCESS_SCOPE]],

          // VDI Monitoring endpoints that require authentication (Azure VM status)
          [`${environment.apiEndpoint}api/vdi-monitoring/`, [API_USER_ACCESS_SCOPE]],
        ]),
      }
    ),
  ],
  providers: [
    { provide: NZ_I18N, useValue: en_US },
    // { provide: DATE_PIPE_DEFAULT_TIMEZONE, useValue: environment.timezone },
    // Normalize/clean URLs before MSAL evaluates protectedResourceMap
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoggingInterceptor,
      multi: true,
    },
    // Use conditional MSAL interceptor instead of standard one
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ConditionalMsalInterceptor,
      multi: true,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    TestService,
    VdiService,
    // Auth Testing Services
    AuthTestingService
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
